import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Chip,
  TablePagination,
  IconButton,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Refresh,
  Visibility,
  Edit,
  PlayArrow,
  Home,
  AttachMoney,
  Business,
  CheckCircle,
  Warning,
  Person,
} from '@mui/icons-material';

const PlotUnhold = () => {
  const canWritePlotUnhold = hasWritePermission('plot-booking.plot-unhold');
  const [searchForm, setSearchForm] = useState({
    loginId: '',
    plotNo: '',
    fromDate: '',
    toDate: '',
  });

  const [showPlotList, setShowPlotList] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [unholdDialogOpen, setUnholdDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [unholdLoading, setUnholdLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Sample plot unhold data - this would come from API
  const [plotUnholdData] = useState([
    {
      id: 1,
      loginId: 'USR001',
      displayName: 'John Doe',
      plotNo: 'P101',
      holdDate: '2025-09-15',
      fromDate: '2025-09-15',
      toDate: '2025-10-15',
      remark: 'Customer requested hold for documentation',
      status: 'Hold',
    },
    {
      id: 2,
      loginId: 'USR002',
      displayName: 'Jane Wilson',
      plotNo: 'P205',
      holdDate: '2025-09-20',
      fromDate: '2025-09-20',
      toDate: '2025-10-20',
      remark: 'Waiting for loan approval',
      status: 'Hold',
    },
    {
      id: 3,
      loginId: 'USR003',
      displayName: 'Mike Johnson',
      plotNo: 'P087',
      holdDate: '2025-09-25',
      fromDate: '2025-09-25',
      toDate: '2025-10-25',
      remark: 'Family discussion pending',
      status: 'Hold',
    },
    {
      id: 4,
      loginId: 'USR004',
      displayName: 'Sarah Davis',
      plotNo: 'P321',
      holdDate: '2025-09-10',
      fromDate: '2025-09-10',
      toDate: '2025-10-10',
      remark: 'Site visit pending',
      status: 'Expired',
    },
    {
      id: 5,
      loginId: 'USR005',
      displayName: 'Robert Chen',
      plotNo: 'P456',
      holdDate: '2025-09-18',
      fromDate: '2025-09-18',
      toDate: '2025-10-18',
      remark: 'Payment arrangement in progress',
      status: 'Hold',
    },
    {
      id: 6,
      loginId: 'USR006',
      displayName: 'David Kumar',
      plotNo: 'P789',
      holdDate: '2025-09-12',
      fromDate: '2025-09-12',
      toDate: '2025-10-12',
      remark: 'Legal verification required',
      status: 'Released',
    },
    {
      id: 7,
      loginId: 'USR007',
      displayName: 'Lisa Patel',
      plotNo: 'P654',
      holdDate: '2025-09-22',
      fromDate: '2025-09-22',
      toDate: '2025-10-22',
      remark: 'Bank loan processing',
      status: 'Hold',
    },
    {
      id: 8,
      loginId: 'USR008',
      displayName: 'Raj Mehta',
      plotNo: 'P432',
      holdDate: '2025-09-08',
      fromDate: '2025-09-08',
      toDate: '2025-10-08',
      remark: 'Property registration delay',
      status: 'Expired',
    },
    {
      id: 9,
      loginId: 'USR009',
      displayName: 'Priya Joshi',
      plotNo: 'P567',
      holdDate: '2025-09-28',
      fromDate: '2025-09-28',
      toDate: '2025-10-28',
      remark: 'Customer out of town',
      status: 'Hold',
    },
    {
      id: 10,
      loginId: 'USR010',
      displayName: 'Amit Verma',
      plotNo: 'P890',
      holdDate: '2025-09-14',
      fromDate: '2025-09-14',
      toDate: '2025-10-14',
      remark: 'Financial arrangement pending',
      status: 'Released',
    },
    {
      id: 11,
      loginId: 'USR011',
      displayName: 'Sneha Agarwal',
      plotNo: 'P123',
      holdDate: '2025-09-26',
      fromDate: '2025-09-26',
      toDate: '2025-10-26',
      remark: 'Document verification in process',
      status: 'Hold',
    },
    {
      id: 12,
      loginId: 'USR012',
      displayName: 'Vikram Shah',
      plotNo: 'P345',
      holdDate: '2025-09-05',
      fromDate: '2025-09-05',
      toDate: '2025-10-05',
      remark: 'EMI restructuring request',
      status: 'Expired',
    },
  ]);

  const handleInputChange = (field, value) => {
    setSearchForm({
      ...searchForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowPlotList(true);
      setPage(0); // Reset to first page when searching
      console.log('Searching with criteria:', searchForm);
      setSuccessMessage('');
      setSearchLoading(false);
    }, 1500);
  };

  const handleReset = () => {
    setSearchForm({
      loginId: '',
      plotNo: '',
      fromDate: '',
      toDate: '',
    });
    setShowPlotList(true);
    setPage(0);
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRecord(null);
  };

  const handleUnhold = (record) => {
    if (record.status !== 'Hold') {
      setErrors({ general: 'Only plots with Hold status can be unhold' });
      return;
    }
    setSelectedRecord(record);
    setUnholdDialogOpen(true);
  };

  const handleCloseUnholdDialog = () => {
    setUnholdDialogOpen(false);
    setSelectedRecord(null);
    setErrors({});
  };

  const handleConfirmUnhold = async () => {
    setUnholdLoading(true);

    // Simulate API call
    setTimeout(() => {
      setSuccessMessage(`Plot ${selectedRecord.plotNo} has been successfully released from hold`);
      setUnholdDialogOpen(false);
      setSelectedRecord(null);
      setErrors({});
      console.log('Unhold plot:', selectedRecord);
      setUnholdLoading(false);
    }, 2000);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Enhanced filter function for plot unhold data
  const getFilteredPlotData = () => {
    if (!showPlotList) return [];

    let filteredData = plotUnholdData;

    // Filter by Login ID
    if (searchForm.loginId.trim()) {
      filteredData = filteredData.filter(plot =>
        plot.loginId.toLowerCase().includes(searchForm.loginId.toLowerCase().trim())
      );
    }

    // Filter by Plot No
    if (searchForm.plotNo.trim()) {
      filteredData = filteredData.filter(plot =>
        plot.plotNo.toLowerCase().includes(searchForm.plotNo.toLowerCase().trim())
      );
    }

    // Filter by date range
    if (searchForm.fromDate) {
      filteredData = filteredData.filter(plot =>
        new Date(plot.holdDate) >= new Date(searchForm.fromDate)
      );
    }

    if (searchForm.toDate) {
      filteredData = filteredData.filter(plot =>
        new Date(plot.holdDate) <= new Date(searchForm.toDate)
      );
    }

    return filteredData;
  };

  // Get search summary for display
  const getSearchSummary = () => {
    const criteria = [];
    if (searchForm.loginId) criteria.push(`Login ID: "${searchForm.loginId}"`);
    if (searchForm.plotNo) criteria.push(`Plot No: "${searchForm.plotNo}"`);
    if (searchForm.fromDate) criteria.push(`From: ${searchForm.fromDate}`);
    if (searchForm.toDate) criteria.push(`To: ${searchForm.toDate}`);

    return criteria.length > 0 ? criteria.join(', ') : 'All Records';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Hold':
        return {
          backgroundColor: '#ff9800',
          color: 'white',
        };
      case 'Released':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      case 'Expired':
        return {
          backgroundColor: '#f44336',
          color: 'white',
        };
      default:
        return {};
    }
  };

  // Calculate statistics
  const totalRecords = plotUnholdData.length;
  const holdCount = plotUnholdData.filter(record => record.status === 'Hold').length;
  const releasedCount = plotUnholdData.filter(record => record.status === 'Released').length;
  const expiredCount = plotUnholdData.filter(record => record.status === 'Expired').length;

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Plot Unhold Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search and manage plot hold releases and status updates
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle />
            {successMessage}
          </Box>
        </Alert>
      )}

      {errors.general && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning />
            {errors.general}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#e3f2fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Home sx={{ color: '#1976d2', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    {totalRecords}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Records
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#fff3e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Warning sx={{ color: '#f57c00', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    {holdCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    On Hold Plots
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#e8f5e9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CheckCircle sx={{ color: '#2e7d32', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    {releasedCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Released Plots
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#ffebee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AttachMoney sx={{ color: '#f44336', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                    {expiredCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expired Holds
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Search sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Plot Hold Records
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Login ID"
                value={searchForm.loginId}
                onChange={(e) => handleInputChange('loginId', e.target.value)}
                placeholder="Enter Login ID"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Plot No"
                value={searchForm.plotNo}
                onChange={(e) => handleInputChange('plotNo', e.target.value)}
                placeholder="Enter Plot Number"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Home color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={searchForm.fromDate}
                onChange={(e) => handleInputChange('fromDate', e.target.value)}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={searchForm.toDate}
                onChange={(e) => handleInputChange('toDate', e.target.value)}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  startIcon={<Search />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                    borderRadius: 2
                  }}
                >
                  {searchLoading ? 'Searching...' : 'Search Records'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleReset}
                  startIcon={<Refresh />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Plot Unhold List Table */}
      {showPlotList && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <PlayArrow sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Plot Hold Records
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search Criteria: {getSearchSummary()}
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              Found {getFilteredPlotData().length} record(s) matching your search criteria
            </Typography>

            {getFilteredPlotData().length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Customer Info</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Plot Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Hold Period</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Remark</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredPlotData()
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((plot, index) => (
                        <TableRow
                          key={plot.id}
                          sx={{
                            '&:hover': { backgroundColor: '#f9fafb' },
                            '& td': { py: 2 }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: '#000' }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                                {plot.loginId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {plot.displayName}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              Plot {plot.plotNo}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Hold Date: {plot.holdDate}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                                {plot.fromDate}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                to {plot.toDate}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography variant="body2" color="text.secondary">
                              {plot.remark}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={plot.status}
                              sx={{ ...getStatusStyle(plot.status), fontWeight: 600, fontSize: '12px' }}
                              size="small"
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleView(plot)}
                                  size="small"
                                  sx={{
                                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                                  }}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              {plot.status === 'Hold' && (
                                <Tooltip title="Release Hold">
                                  <IconButton
                                    color="success"
                                    onClick={() => handleUnhold(plot)}
                                    size="small"
                                    sx={{
                                      '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                                    }}
                                  >
                                    <PlayArrow />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <TablePagination
                  
                  component="div"
                  count={getFilteredPlotData().length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: '1px solid #e0e0e0' }}
                />
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Alert severity="info">
                  <Typography variant="h6" gutterBottom>
                    No Records Found
                  </Typography>
                  <Typography variant="body2">
                    No plot hold records match your search criteria: {getSearchSummary()}
                  </Typography>
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#000',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Visibility sx={{ color: '#1976d2', fontSize: 28 }} />
            Plot Hold Details
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedRecord && (
            <Box>
              {/* Customer Information */}
              <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Customer Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Login ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.loginId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Display Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.displayName}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Plot Information */}
              <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Plot Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Plot Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.plotNo}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Hold Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.holdDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Hold Period</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.fromDate} to {selectedRecord.toDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Current Status</Typography>
                    <Chip
                      label={selectedRecord.status}
                      sx={{ ...getStatusStyle(selectedRecord.status), fontWeight: 600 }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Remarks */}
              <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Remarks
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedRecord.remark}
                </Typography>
              </Card>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unhold Confirmation Dialog */}
      <Dialog
        open={unholdDialogOpen}
        onClose={handleCloseUnholdDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#000',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <PlayArrow sx={{ color: '#2e7d32', fontSize: 28 }} />
            Release Plot Hold
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedRecord && (
            <Box>
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Are you sure you want to release the hold on plot {selectedRecord.plotNo}?
                </Typography>
              </Alert>

              <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Hold Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Customer</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.displayName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Plot Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.plotNo}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Hold Period</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.fromDate} to {selectedRecord.toDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Reason</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRecord.remark}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseUnholdDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          {/* {canWritePlotUnhold && ( */}
          <Button
            onClick={handleConfirmUnhold}
            color="success"
            variant="contained"
            disabled={unholdLoading}
            startIcon={<PlayArrow />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
            }}
          >
            {unholdLoading ? 'Releasing...' : 'Release Hold'}
          </Button>
          {/* )} */}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlotUnhold;